<template>
  <TabbedBox>
    <template v-slot:tablist>
      <Tab :first="true" tabid="tab-production">
        <i class="far fa-code-branch"></i> Production Tree
      </Tab>
      <Tab tabid="tab-production-settings">
        <i class="far fa-cogs"></i> Production Settings
      </Tab>
    </template>
    <template v-slot:tabcontent>
      <TabPage :first="true" tabid="tab-production">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="!productionTree.editing" class="float-right">
              <DropdownMenuItem v-if="userHasCreatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" @click="newProductionStage" icon="fa-plus">New Production Stage</DropdownMenuItem>
              <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" @click="productionTree.editing = true" icon="fa-pencil">Edit Stage Order</DropdownMenuItem>
            </ActionDropMenu>
            <h3>Production Tree</h3>
          </div>
        </div>
        <FormGroup :labelColumnSize="6" :form="productionTree.form">
          <StaticDataTable class="static-table-list production-tree-list" :editing="productionTree.editing" :hover="true" :striped="false" @sortUpdated="productionTree.sortOrder = $event" :key="productionTree.updateKey">
            <template v-slot:header>
              <tr>
                <th v-if="productionTree.editing"></th>
                <th>Name</th>
                <th>Category / Type</th>
                <th>Location</th>
                <th>Scanning Options</th>
                <th class="text-center">Active</th>
                <th v-if="!productionTree.editing">View</th>
                <th v-if="!productionTree.editing">Actions</th>
              </tr>
            </template>
            <template v-slot:body>
              <template v-for="(stage, stageIndex) in productionTree.stageList">
                <tr v-if="stageIndex > 0 && !productionTree.editing" class="order-item-spacer" :key="`item-${stageIndex}-spacer`">
                  <td colspan="6"> </td>
                </tr>

                <tr :key="stage.id" :data-sort-id="stage.id" class="static-table-sortable production-tree-row" :style="getTreeGroupRowStyles(stage)">
                  <td v-if="productionTree.editing" width="16px">
                    <div class="static-datatable-row-draggable"><i class="fal fa-bars"></i> </div>
                  </td>
                  <td class="font-weight-bold">
                    <LinkProductionStage :id="stage.id" :name="`Stage - ${stage.name}`" :newTab="false" />
                    <LabelBadge v-if="stage.isExternal" labelClass="label-production-stage-external">External</LabelBadge>
                  </td>
                  <td>{{stage.category | formatLookupName(ProductionStageCategoryTypeLookup)}}</td>
                  <td> </td>
                  <td> </td>
                  <td class="text-center">
                    <IconBoolean :value="stage.isActive" :displayFalse="true" />
                  </td>
                  <td> </td>
                  <td>
                    <ActionDropMenu :tableAction="true" :forceDropdown="true">
                      <DropdownMenuItem v-if="userHasCreatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig)" @click="newProductionStation(stage.id)" icon="fa-plus">Add Station</DropdownMenuItem>
                    </ActionDropMenu>
                  </td>
                </tr>
                <template v-if="!productionTree.editing">
                  <tr v-for="station in stage.productionStations" class="production-tree-row" :key="`stage-${stage.id}-station-${station.id}`" :style="getTreeGroupRowStyles(stage)">
                    <td>
                      <i class="fas fa-caret-right mr-2 ml-3"></i>
                      <LinkProductionStation :id="station.id" :name="station.name" :newTab="false" />
                      <template v-if="station.secondaryStation">
                        <i class="fas fa-long-arrow-right mr-3 ml-3"></i>
                        <span class="font-italic">{{station.secondaryStation.name}}</span>
                      </template>
                      <LabelBadge labelClass="label-production-maintenance" class="ml-3" v-if="station.isMaintenanceMode"><i class="fas fa-wrench mr-1"></i> In Maintenance</LabelBadge>
                    </td>
                    <td>
                      {{station.type | formatLookupName(ProductionStationTypeLookup)}}
                    </td>
                    <td>
                      {{station.accountLocation ? station.accountLocation.name : '-'}}
                    </td>
                    <td>
                      <LabelBadge labelClass="label-production-scanning-in mr-1" v-if="station.isScanInAvailable"><i class="fas fa-arrow-right mr-1"></i> Scan In</LabelBadge>
                      <LabelBadge labelClass="label-production-scanning-out" v-if="station.isScanOutAvailable"><i class="fas fa-check-circle mr-1"></i> Scan Out</LabelBadge>
                    </td>
                    <td class="text-center">
                      <IconBoolean :value="station.isActive" :displayFalse="true" />
                    </td>
                    <td>
                      <LinkButton 
                        v-if="userHasReadPermission(PermissionTypes.ProductionPermissionTypes.productionStationApp)"
                        :href="`/apps/glass-production-station/${station.id}/stage/${stage.id}`"
                        :newTab="true"
                        colourClass="btn-default"
                        icon="fa-desktop">
                        Station View
                      </LinkButton>
                    </td>
                    <td> </td>
                  </tr>
                  
                </template>
              </template>
            </template>
          </StaticDataTable>

          <FormGroup>
            <div v-if="productionTree.editing">
              <FormHorizontalRule />
              <FormSubmit label="Update" :submitting="productionTree.submitting" @cancelled="cancelProductionTreeEditing" @submitted="submitProductionTreeEditing"></FormSubmit>
            </div>
          </FormGroup>
        </FormGroup>
        
      </TabPage>

      <TabPage tabid="tab-production-settings">
        <div class="row">
          <div class="col-lg-12">
            <ActionDropMenu v-if="userHasUpdatePermission(PermissionTypes.ProductionPermissionTypes.productionConfig) && !productionSettings.editing" class="float-right">
              <DropdownMenuItem @click="productionSettings.editing = true" icon="fa-pencil">Edit Production Settings</DropdownMenuItem>
            </ActionDropMenu>
            <ContentTitle>Production Settings</ContentTitle>
            <ContentSubTitle>General settings for the Production module</ContentSubTitle>
          </div>
        </div>
        <FormGroup :labelColumnSize="6" :form="productionSettings.form">
          <FormAccountSetting label="Toughening Batch Expiry Time" helpDefault="60mins">
            <template v-slot:helpContent>
              <p>How long each toughening batch lasts for (in minutes) until a new batch needs to be created.</p>
            </template>
            <template v-slot:entry>
              <span v-if="!productionSettings.editing">{{getConfigSettingInt(productionSettings.settings, AccountSettingKeys.productionTougheningBatchExpiryTime)}} minutes</span>
              <FormInputText type="number" v-if="productionSettings.editing" :value="getConfigSettingInt(productionSettings.settings, AccountSettingKeys.productionTougheningBatchExpiryTime)" @input="setConfigSetting(productionSettings.settings, AccountSettingKeys.productionTougheningBatchExpiryTime, $event)" addon="mins" />
            </template>
          </FormAccountSetting>

          <FormAccountSetting label="Toughening Batch Grace Time" helpDefault="30mins">
            <template v-slot:helpContent>
              <p>The grace period (in minutes) after a toughening batch expires that panels can still be scanned and advanced through the toughener. After this time, a new batch must be created.</p>
              <p>During the grace period, the batch will be flagged as expired with a warning for last chance processing.</p>
            </template>
            <template v-slot:entry>
              <span v-if="!productionSettings.editing">{{getConfigSettingInt(productionSettings.settings, AccountSettingKeys.productionTougheningBatchGraceTime)}} minutes</span>
              <FormInputText type="number" v-if="productionSettings.editing" :value="getConfigSettingInt(productionSettings.settings, AccountSettingKeys.productionTougheningBatchGraceTime)" @input="setConfigSetting(productionSettings.settings, AccountSettingKeys.productionTougheningBatchGraceTime, $event)" addon="mins" />
            </template>
          </FormAccountSetting>

          <FormAccountSetting label="Production Transfer Lead Time" helpDefault="480 Mins">
            <template v-slot:helpContent>
              <p>The lead time required when transfering panels between multiple site locations when production stations are split amongst multiple site locations</p>
            </template>
            <template v-slot:entry>
              <span v-if="!productionSettings.editing">{{getConfigSettingInt(productionSettings.settings, AccountSettingKeys.productionTransferLeadTime)}} minutes</span>
              <FormInputText type="number" v-if="productionSettings.editing" :value="getConfigSettingInt(productionSettings.settings, AccountSettingKeys.productionTransferLeadTime)" @input="setConfigSetting(productionSettings.settings, AccountSettingKeys.productionTransferLeadTime, $event)" addon="mins" />
            </template>
          </FormAccountSetting>

          <FormAccountSetting label="Production Schedule Handles Public Holidays" helpDefault="Enabled">
            <template v-slot:helpContent>
              <p>If enabled, the production scheduler will skip public holidays when generating time and date estimates.<br />
              Public holidays will be automatically calculated based on the account country &amp; state codes.</p>
            </template>
            <template v-slot:entry>
              <IconBoolean v-if="!productionSettings.editing" :value="getConfigSettingBoolean(productionSettings.settings, AccountSettingKeys.productionScheduleUsePublicHolidays)" />
              <FormCheckbox v-if="productionSettings.editing" :value="getConfigSettingBoolean(productionSettings.settings, AccountSettingKeys.productionScheduleUsePublicHolidays)" @change="setConfigSetting(productionSettings.settings, AccountSettingKeys.productionScheduleUsePublicHolidays, $event)" />
            </template>
          </FormAccountSetting>

          
          <FormSubmit v-if="productionSettings.editing" label="Update" :submitting="productionSettings.submitting" @submitted="submitProductionConfigSettings" @cancelled="cancelProductionConfigSettings" />
        </FormGroup>
      </TabPage>
    </template>
  </TabbedBox>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import DataDisplayMixin from '@/mixins/dataDisplay'

import ProductionStageDialog from '@/modules/production/views/productionStageDialog'
import ProductionStationDialog from '@/modules/production/views/productionStationDialog'

import { ProductionStageCategoryTypeLookup, ProductionStationTypeLookup, AccountSettingKeys } from '@/store/types'

export default {
  name: 'ProductionConfiguration',
  mixins: [form, DataDisplayMixin],
  components: {
  },
  data: () => {
    return {
      contactLookup: [],

      productionTree: {
        editing: false,
        submitting: false,
        updateKey: 0,
        sortOrder: [],
        form: {},
        stageList: [],
        stationList: []
      },

      productionSettings: {
        editing: false,
        submitting: false,
        form: {},
        settings: {}
      }
    }
  },
  created() {
    this.ProductionStageCategoryTypeLookup = ProductionStageCategoryTypeLookup
    this.ProductionStationTypeLookup = ProductionStationTypeLookup
    this.AccountSettingKeys = AccountSettingKeys
  },
  mounted() {
    this.requestData()
  },
  computed: {
    
  },
  methods: {
    reloadData() {
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.productionService.getProductionStageList(),
        this.$apiService.productionService.getProductionStationList(),
        this.$apiService.productionService.getConfigSettings(),
      ]))
      .then(results => {
        this.productionTree.stageList = results[0].data
        this.productionTree.stationList = results[1].data

        this.productionTree.sortOrder = this.productionTree.stageList.map(stage => stage.id)

        this.$data.productionSettings.settings = results[2].data
      })
      .catch(err => this.handlePageLoadError(err))
    },

    newProductionStage() {
      this.displayModal(ProductionStageDialog, { 
        isCreateMode: true
      })
      .then(stageId => {
        if(stageId) {
          this.successToast('Production Stage Added')
          this.$router.push(`/production/stages/${stageId}`)
        }
      })
    },
    newProductionStation(productionStageId = null) {
      this.displayModal(ProductionStationDialog, { 
        isCreateMode: true,
        productionStageId: productionStageId
      })
      .then(stationId => {
        if(stationId) {
          this.successToast('Production Station Added')
          this.$router.push(`/production/stations/${stationId}`)
        }
      })
    },

    // Production Tree
    scanTypeDescription(station) {
      if(station.isScanInAvailable && station.isScanOutAvailable)
        return 'In and Out'
      if(station.isScanInAvailable)
        return 'In Only'
      if(station.isScanOutAvailable)
        return 'Out Only'
      return 'None'
    },
    cancelProductionTreeEditing() {
      this.productionTree.editing = false
      this.productionTree.submitting = false

      this.$nextTick(() => {
        // JB: This is hacky, but cannot seem to get the table data to reset after sortable()
        this.productionTree.updateKey++
      })

      // Refresh the production tree
      this.reloadData()
    },
    submitProductionTreeEditing() {
      this.productionTree.submitting = true

      var productionStageBatchData = this.productionTree.sortOrder.map((stageId, index) => {
        return {
          id: stageId,
          sortOrder: index
        }
      })

      this.$apiService.productionService.updateProductionStageBatch({ stages: productionStageBatchData })
      .then(response => {
        this.productionTree.editing = false
        this.productionTree.submitting = false
        this.reloadData()
      })
      .catch(err => {
        this.displayNetworkError(err, 'Failed to update', 'Error while attempting to update stage order')                
      })
      .finally(() => {
        this.productionTree.submitting = false
      })
    },

    getTreeGroupRowStyles(group) {
      return {
        'border-left': `4px solid ${group.highlightColour}`
      }
    },

    // General Config Settings
    getConfigSetting(settingsData, settingKey, defaultValue = '') {
      if(settingsData[settingKey] !== undefined)
        return settingsData[settingKey]
      return defaultValue
    },
    getConfigSettingBoolean(settingsData, settingKey, defaultValue = false) {
      if(settingsData[settingKey] !== undefined)
        return this.parseBoolean(settingsData[settingKey])
      return defaultValue
    },
    getConfigSettingInt(settingsData, settingKey, defaultValue = 0) {
      if(settingsData[settingKey] !== undefined)
        return parseInt(settingsData[settingKey])
      return defaultValue
    },
    getConfigSettingNumber(settingsData, settingKey, defaultValue = 0) {
      if(settingsData[settingKey] !== undefined)
        return this.parseDecimal(settingsData[settingKey])
      return defaultValue
    },
    setConfigSetting(settingsData, settingKey, value) {
      settingsData[settingKey] = value
    },

    submitProductionConfigSettings() {
      this.$data.productionSettings.submitting = true
      this.$apiService.productionService.updateConfigSettings({ settings: this.$data.productionSettings.settings })
      .then(response => {
        this.$data.productionSettings.editing = false
        this.successToast('Production Config Settings updated')
      })
      .catch(err => {
        this.$data.productionSettings.form = this.processFormResponse(err)
      })
      .finally(() => {
        this.$data.productionSettings.submitting = false
      })
    },
    cancelProductionConfigSettings() {
      this.$data.productionSettings.editing = false
      this.$apiService.productionService.getConfigSettings()
      .then(response => {
        this.$data.productionSettings.settings = response.data
      })
    },
    

  }
}

</script>